// ── StormAtlas Spotter Network — data layer ───────────────────────────────────
// Mock data today; swap MOCK_SPOTTERS / MOCK_REPORTS for API responses later.
// GeoJSON converters keep property names stable so Mapbox expressions and the
// popup component never need to change when real data arrives.

export const REPORT_CONFIG = {
  tornado:   { label: "Tornadoes & Waterspouts", color: "#f43f5e", abbr: "TOR" },
  funnel:    { label: "Funnel Clouds",            color: "#fb923c", abbr: "FUN" },
  hail:      { label: "Hail",                     color: "#f59e0b", abbr: "HAI" },
  wind:      { label: "Wind Damage",              color: "#a78bfa", abbr: "WND" },
  downburst: { label: "Downbursts/High Winds",    color: "#818cf8", abbr: "DWN" },
  flood:     { label: "Flooding",                 color: "#60a5fa", abbr: "FLD" },
  rain:      { label: "Heavy Snow/Rain",           color: "#38bdf8", abbr: "SNW" },
};

export const REPORT_SOURCES = {
  mping:      { label: "mPING",      strokeColor: "#06b6d4", strokeWidth: 1.5, radius: 8 },
  spotter:    { label: "Spotter",    strokeColor: "#f59e0b", strokeWidth: 2.5, radius: 9 },
  stormatlas: { label: "StormAtlas", strokeColor: "#38bdf8", strokeWidth: 2,   radius: 8 },
};

// ── mPING integration ────────────────────────────────────────────────────────

// mPING category → StormAtlas type
const MPING_TYPE_MAP = {
  "Hail":         "hail",
  "Rain":         "rain",
  "Snow":         "rain",
  "Wind Damage":  "wind",
  "Tornado":      "tornado",
  "Funnel Cloud": "funnel",
  "Flooding":     "flood",
  "Waterspout":   "tornado",
};

// Fallback mock mPING reports (used when real endpoint is unavailable)
const MOCK_MPING_REPORTS = [
  { id: "mp-001", type: "hail",      source: "mping", lat: 35.44, lon: -97.52,  createdAt: new Date(Date.now() -  3*60000).toISOString(), spotterName: "mPING", note: "Quarter size hail",            severity: "moderate"    },
  { id: "mp-002", type: "rain",      source: "mping", lat: 36.30, lon: -96.75,  createdAt: new Date(Date.now() - 11*60000).toISOString(), spotterName: "mPING", note: "Heavy rain, poor visibility",   severity: "minor"       },
  { id: "mp-003", type: "wind",      source: "mping", lat: 35.70, lon: -98.30,  createdAt: new Date(Date.now() - 18*60000).toISOString(), spotterName: "mPING", note: "Measured 58 mph gust",          severity: "moderate"    },
  { id: "mp-004", type: "tornado",   source: "mping", lat: 35.15, lon: -97.80,  createdAt: new Date(Date.now() - 25*60000).toISOString(), spotterName: "mPING", note: "Brief ground contact",          severity: "significant" },
  { id: "mp-005", type: "hail",      source: "mping", lat: 37.40, lon: -97.20,  createdAt: new Date(Date.now() - 33*60000).toISOString(), spotterName: "mPING", note: "Nickel to quarter size",        severity: "minor"       },
  { id: "mp-006", type: "flood",     source: "mping", lat: 36.55, lon: -99.80,  createdAt: new Date(Date.now() - 41*60000).toISOString(), spotterName: "mPING", note: "Road flooding, 6\" of water",   severity: "moderate"    },
  { id: "mp-007", type: "rain",      source: "mping", lat: 34.75, lon: -99.10,  createdAt: new Date(Date.now() - 50*60000).toISOString(), spotterName: "mPING", note: "Heavy snow, 1\" accumulation",  severity: "minor"       },
  { id: "mp-008", type: "downburst", source: "mping", lat: 36.90, lon: -100.20, createdAt: new Date(Date.now() - 55*60000).toISOString(), spotterName: "mPING", note: "Tree down, power outage",       severity: "moderate"    },
];

function computeReportOpacity(createdAt) {
  const ageMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
  if (ageMin >= 60) return 0;
  if (ageMin >= 45) return 0.35;
  if (ageMin >= 30) return 0.65;
  return 1.0;
}

// ── Mock data ────────────────────────────────────────────────────────────────
// Spotters scattered across the Southern/Central Plains.

export const MOCK_SPOTTERS = [
  { id: "sp-001", name: "Alex T.",   lat: 35.22, lon: -97.44, lastUpdated: "2025-04-09T19:30:00Z", status: "Chasing",    verified: true  },
  { id: "sp-002", name: "Maria S.",  lat: 36.15, lon: -96.82, lastUpdated: "2025-04-09T19:15:00Z", status: "Stationary", verified: true  },
  { id: "sp-003", name: "Dan K.",    lat: 35.47, lon: -98.10, lastUpdated: "2025-04-09T18:55:00Z", status: "Chasing",    verified: false },
  { id: "sp-004", name: "Lexi R.",   lat: 37.68, lon: -97.33, lastUpdated: "2025-04-09T19:25:00Z", status: "Chasing",    verified: true  },
  { id: "sp-005", name: "Tom B.",    lat: 34.92, lon: -99.40, lastUpdated: "2025-04-09T19:10:00Z", status: "Stationary", verified: false },
  { id: "sp-006", name: "Jenna W.",  lat: 36.79, lon: -100.03, lastUpdated: "2025-04-09T19:00:00Z", status: "Chasing",   verified: true  },
];

// Non-mPING seed reports (mping source comes from fetchMPingReports)
export const MOCK_REPORTS = [
  { id: "rp-001", type: "tornado", source: "spotter",    lat: 35.31, lon: -97.55,  createdAt: new Date(Date.now() - 8*60000).toISOString(),  spotterName: "Alex T.",  note: "Brief rope tornado, ~50 m wide, moving NE", severity: "significant" },
  { id: "rp-004", type: "rain",    source: "spotter",    lat: 37.55, lon: -97.44,  createdAt: new Date(Date.now() - 12*60000).toISOString(), spotterName: "Lexi R.",  note: "2\" in 30 min, ponding on roadways",          severity: "minor"       },
  { id: "rp-005", type: "hail",    source: "stormatlas", lat: 34.85, lon: -99.30,  createdAt: new Date(Date.now() - 45*60000).toISOString(), spotterName: "Tom B.",   note: "Nickel sized, coating roads",                severity: "minor"       },
  { id: "rp-006", type: "funnel",  source: "spotter",    lat: 36.68, lon: -100.10, createdAt: new Date(Date.now() - 52*60000).toISOString(), spotterName: "Jenna W.", note: "Funnel extending 2/3 from cloud base",       severity: "significant" },
];

// ── GeoJSON converters ───────────────────────────────────────────────────────
// All feature properties are primitives so Mapbox serialises them cleanly.

export function spottersToGeoJson(spotters) {
  return {
    type: "FeatureCollection",
    features: spotters.map((s) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [s.lon, s.lat] },
      properties: {
        id:          s.id,
        name:        s.name,
        status:      s.status ?? "Unknown",
        lastUpdated: s.lastUpdated,
        verified:    s.verified ? 1 : 0,
      },
    })),
  };
}

export function reportsToGeoJson(reports) {
  return {
    type: "FeatureCollection",
    features: reports.map((r) => {
      const src = REPORT_SOURCES[r.source ?? "mping"] ?? REPORT_SOURCES.mping;
      return {
        type: "Feature",
        geometry: { type: "Point", coordinates: [r.lon, r.lat] },
        properties: {
          id:               r.id,
          type:             r.type,
          abbr:             REPORT_CONFIG[r.type]?.abbr  ?? "RPT",
          color:            REPORT_CONFIG[r.type]?.color ?? "#94a3b8",
          label:            REPORT_CONFIG[r.type]?.label ?? r.type,
          spotterName:      r.spotterName,
          createdAt:        r.createdAt,
          note:             r.note ?? "",
          severity:         r.severity ?? "unknown",
          source:           r.source ?? "mping",
          sourceLabel:      src.label,
          sourceStrokeColor: src.strokeColor,
          sourceStrokeWidth: src.strokeWidth,
          sourceRadius:     src.radius,
          opacity:          computeReportOpacity(r.createdAt),
        },
      };
    }),
  };
}

// ── mPING fetch ──────────────────────────────────────────────────────────────

export async function fetchMPingReports() {
  // Real endpoint: https://mping.ou.edu/mping/api/v2/reports/?dur=60&format=json
  // Requires API key + likely CORS blocked from browser — mock fallback until
  // a server-side proxy is available. Swap the return below for a real fetch().
  const cutoff = Date.now() - 60 * 60 * 1000;
  return MOCK_MPING_REPORTS.filter((r) => new Date(r.createdAt).getTime() > cutoff);
}

// Exported for use if real API normalization is wired up later
export { MPING_TYPE_MAP };
